import React, { useEffect, useRef, useContext, useState, useMemo, useCallback } from 'react';
import { PatientDataContext } from '../../../context/PatientDataContext';
import { useParams } from 'react-router-dom';
import Layout from '../../../components/Layout';
import Breadcrumb from '../../../components/Breadcrumb';
import PADSideNavbar from '../../providers/9_postAssessmentDiscussion/components/PADSideNavbar';
import { CheckIcon, PlusIcon, CheckCircleIcon } from '@heroicons/react/solid';
import ModularStepFooter from '../../../components/autismModular/ModularStepFooter';
import { SearchIcon, XIcon } from '@heroicons/react/outline';
import * as Dialog from '@radix-ui/react-dialog';
import { FormattedAnswer, normalizeRawAnswer } from '../../../utils/formDisplayUtils';
import InfoTooltip from '../../../components/InfoTooltip';
import { useCanEditStage } from '../../../hooks/useCanEditStage';
import { useIsPreviewMode } from '../../../hooks/useIsPreviewMode';
import { usePreviewAwareNavigate } from '../../../hooks/usePreviewAwareNavigate';
import { useAuth } from '../../../hooks/useAuth';
import useWorkflowConfig from '../../../hooks/useWorkflowConfig';
import { getDifferentialDiagnoses } from '../../../utils/diagnosticDecisionRouting';
import { isPadAdhdOutcomePositive, getPatientSexAtBirth } from '../../../utils/conditionChecks';
import DictationTextArea from '../../../components/DictationTextArea';
import ButtonSpinner from '../../../components/informantManagement/ButtonSpinner';
import { TriggerGenerateDocumentSection } from '../../../utils/TriggerGenerateDocumentSection';
import { useSectionExitRegeneration } from '../../../hooks/useSectionExitRegeneration';
import { auth } from '../../../firebase';
import { serverTimestamp } from 'firebase/firestore';
import { buildFirstCompletedFields, buildFirstGeneratedFields, buildPadCompletionFields, appendReportAuditEntry } from '../../../utils/reportAuditHelpers';
import { recordInferredAppointmentDateOnCompletion } from '../../../utils/appointmentDateHelpers';
import ModuleCompletionDialog, { CancelButton, GenerateReportButton, SummarySection } from '../../../components/ModuleCompletionDialog';
import GenerateReportProgressDialog from '../../../components/GenerateReportProgressDialog';
import PatientContractTypeBadge from '../../../components/PatientContractTypeBadge';

/**
 * CYP ADHD PAD Next Steps — placeholder page for The Owl Centre CYP patients.
 *
 * This is a mirror of the adult TOC "Next Steps" page
 * (9_postAssessmentDiscussion/6_PAD_TOC.js) adapted for the CYP ADHD route
 * namespace. It presents the clinician with Yes/No report-inclusion decisions
 * whose answers control conditional content blocks in the patient report.
 *
 * Placeholder (Oct 2026): the content and decisions are identical to the
 * adult version. TOC will design CYP-specific questions in a future sprint;
 * when that happens, only this file needs to change — the route, navigation,
 * and bridge wiring are already in place.
 *
 * Decisions are persisted at:
 *   dataModel.5_postAssessmentDiscussion.sections.6
 *
 * The report rendering Cloud Function reads these values to determine which
 * conditional blocks to include/exclude from the final patient report.
 */
export default function CypAdhdPadNextSteps() {
  const { patientId } = useParams();
  const navigate = usePreviewAwareNavigate();
  const isPreviewMode = useIsPreviewMode();
  const { patientDataModel, updatePatientFirestore, flushPendingUpdates } = useContext(PatientDataContext);
  const { claims } = useAuth();
  const wf = useWorkflowConfig(patientDataModel, claims);
  const { isAppointmentDatesEnabled = false, skipDifferentialsDecisions } = wf;
  const [hasChanges, setHasChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isNavigating, setIsNavigating] = useState(false);

  // Free-text rationale for declining a Workplace Needs Assessment (Lexxic only).
  // Persisted at dataModel.5_postAssessmentDiscussion.sections.6.wnaDeclinedRationale
  // and used as LLM input to generate a patient-facing paragraph for the report.
  const [wnaDeclinedRationale, setWnaDeclinedRationale] = useState('');

  const canEdit = useCanEditStage(patientDataModel, 'PAD', { previewMode: isPreviewMode });

  // ── Navigation constants ───────────────────────────────────────────────
  // CYP routes live under /proforma/cyp-adhd/ rather than /proforma/.
  const currentStageTitle = 'CYP ADHD Post-Assessment Discussion';
  const sectionKey = '6';
  const displayNumber = 2;
  const currentSectionTitle = 'Next Steps';
  const sectionType = 'toc_next_steps';

  // The patient's contract type determines which conditional questions appear.
  const contractType = patientDataModel?.customFields?.assessmentType || '';
  const isWorkplaceNeedsAssessmentContract = contractType === 'Lexxic Ltd';

  const isDiagnosisPositive = useMemo(
    () => isPadAdhdOutcomePositive(patientDataModel),
    [patientDataModel]
  );

  // Whether autism is listed as an emerging differential for this patient.
  const isAutismEmergingDifferential = useMemo(() => {
    const differentials = getDifferentialDiagnoses(patientDataModel);
    return differentials.some((name) => name.toLowerCase().includes('autism'));
  }, [patientDataModel]);

  // Autism follow-up panel: active when differentials decisions are disabled
  // (TOC) and autism is an emerging differential.
  const isAutismFollowUpActive = skipDifferentialsDecisions && isAutismEmergingDifferential;

  // Redirect guard: this page is only accessible for patients with a positive
  // ADHD diagnosis. A negative diagnosis ends the PAD at Assessment Outcome.
  useEffect(() => {
    if (!patientDataModel) return;
    if (!isDiagnosisPositive) {
      navigate(
        `/patient/${patientId}/proforma/cyp-adhd/post-assessment-discussion/review-assessment`,
        { replace: true }
      );
    }
  }, [patientDataModel, isDiagnosisPositive, navigate, patientId]);

  const patientSexAtBirth = useMemo(
    () => getPatientSexAtBirth(patientDataModel),
    [patientDataModel]
  );

  const [isSexDialogOpen, setIsSexDialogOpen] = useState(false);

  // Extracts the sex-at-birth question's raw form data for the source dialog.
  const sexAtBirthRawPrefillData = useMemo(() => {
    const yourInfo = patientDataModel?.formData?.yourInformation;
    if (!yourInfo?.formSubsections) return [];

    for (const sub of Object.values(yourInfo.formSubsections)) {
      for (const q of Object.values(sub.questions || {})) {
        if (q.questionVarName === 'sexAssignedAtBirth') {
          const sq = q.subquestions
            ? Object.values(q.subquestions).find(s => s.serviceUserVarName === 'sexAssignedAtBirth')
            : null;

          const rawAnswer = sq?.answer ? normalizeRawAnswer(sq.answer) : null;
          const displayAnswer = sq?.answer?.value || null;

          return [{
            formName: 'Your Information',
            questionTitle: q.questionTitle || 'Sex assigned at birth',
            question: sq?.subquestionDisplayText || 'At birth, what was your recorded sex?',
            answer: displayAnswer || 'No answer given',
            rawAnswer,
            hasAnswer: !!displayAnswer,
            options: sq?.options || [],
          }];
        }
      }
    }
    return [];
  }, [patientDataModel?.formData?.yourInformation]);

  const [decisions, setDecisions] = useState({
    wantsMedication: null,
    benefitsFromWorkplaceAssessment: null,
    includeAutismFollowUp: null,
    includeDisclosureWork: null,
    includeDisclosureEducation: null,
    includeDisclosureFriendsFamily: null,
    includeDriving: null,
    includeRelationships: null,
    includeFemalesWithADHD: null,
    includeExerciseDiet: null,
    includeEducationalAdvice: null,
    includeEmploymentAdvice: null,
    includeCareNeedsAssessment: null,
  });

  // One-shot seed from Firestore — only runs once when data first arrives.
  const isInitialLoad = useRef(true);
  useEffect(() => {
    if (!isInitialLoad.current) return;
    const saved = patientDataModel?.dataModel?.['5_postAssessmentDiscussion']?.sections?.[sectionKey];
    if (!saved && !patientDataModel) return;

    isInitialLoad.current = false;
    const s = saved || {};
    setDecisions({
      wantsMedication: s.wantsMedication ?? null,
      benefitsFromWorkplaceAssessment: s.benefitsFromWorkplaceAssessment ?? null,
      includeAutismFollowUp: s.includeAutismFollowUp ?? null,
      includeDisclosureWork: s.includeDisclosureWork ?? null,
      includeDisclosureEducation: s.includeDisclosureEducation ?? null,
      includeDisclosureFriendsFamily: s.includeDisclosureFriendsFamily ?? null,
      includeDriving: s.includeDriving ?? null,
      includeRelationships: s.includeRelationships ?? null,
      includeFemalesWithADHD: s.includeFemalesWithADHD ?? null,
      includeExerciseDiet: s.includeExerciseDiet ?? null,
      includeEducationalAdvice: s.includeEducationalAdvice ?? null,
      includeEmploymentAdvice: s.includeEmploymentAdvice ?? null,
      includeCareNeedsAssessment: s.includeCareNeedsAssessment ?? null,
    });
    setWnaDeclinedRationale(s.wnaDeclinedRationale || '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientDataModel]);

  // Whether there is meaningful LLM work to trigger.
  const hasWnaParagraphToGenerate = isWorkplaceNeedsAssessmentContract
    && decisions.benefitsFromWorkplaceAssessment === false
    && !!wnaDeclinedRationale.trim();

  const { markRegenHandled } = useSectionExitRegeneration({
    patientId,
    stageTitle: currentStageTitle,
    sectionTitle: currentSectionTitle,
    hasChanges,
    canEdit,
    patientDataModel,
    shouldRegenerate: hasWnaParagraphToGenerate,
  });

  const handleWnaRationaleBlur = useCallback(() => {
    if (!canEdit) return;
    updatePatientFirestore(patientId, {
      '5_postAssessmentDiscussion': {
        sections: {
          [sectionKey]: {
            sectionTitle: currentSectionTitle,
            sectionType,
            wnaDeclinedRationale,
          },
        },
      }
    });
  }, [canEdit, patientId, updatePatientFirestore, wnaDeclinedRationale]);

  const handleDecision = useCallback((key, value) => {
    if (!canEdit) return;

    setDecisions(prev => ({ ...prev, [key]: value }));

    updatePatientFirestore(patientId, {
      '5_postAssessmentDiscussion': {
        sections: {
          [sectionKey]: {
            sectionTitle: currentSectionTitle,
            sectionType,
            [key]: value,
          },
        },
      }
    });
  }, [canEdit, patientId, updatePatientFirestore]);

  // ── CYP-specific routes ────────────────────────────────────────────────
  const prevSectionLink = '/proforma/cyp-adhd/post-assessment-discussion/review-assessment';

  // All questions must be answered before Finish is enabled.
  const isFormComplete = useMemo(() => {
    if (decisions.wantsMedication === null) return false;
    if (isWorkplaceNeedsAssessmentContract && decisions.benefitsFromWorkplaceAssessment === null) return false;
    if (isWorkplaceNeedsAssessmentContract && decisions.benefitsFromWorkplaceAssessment === false && !wnaDeclinedRationale.trim()) return false;
    if (isAutismFollowUpActive && decisions.includeAutismFollowUp === null) return false;
    // Care needs is always shown on this page (positive ADHD only) and always
    // required. Missing/unanswered means the report omits the paragraph.
    if (decisions.includeCareNeedsAssessment === null) return false;
    if (decisions.includeDisclosureWork === null) return false;
    if (decisions.includeDisclosureEducation === null) return false;
    if (decisions.includeDisclosureFriendsFamily === null) return false;
    if (decisions.includeDriving === null) return false;
    if (decisions.includeRelationships === null) return false;
    if (decisions.includeFemalesWithADHD === null) return false;
    if (decisions.includeExerciseDiet === null) return false;
    if (decisions.includeEducationalAdvice === null) return false;
    if (decisions.includeEmploymentAdvice === null) return false;
    return true;
  }, [isWorkplaceNeedsAssessmentContract, isAutismFollowUpActive, decisions, wnaDeclinedRationale]);

  // Panel numbering adjusts when the autism follow-up panel is visible.
  const showAutismFollowUpPanel = skipDifferentialsDecisions;
  // Care needs is always present, so it occupies the slot after workplace
  // (or after autism follow-up when that panel is visible) and shifts the
  // later panels down by one.
  const panelNumbers = showAutismFollowUpPanel
    ? { medication: 1, workplace: 2, autismFollowUp: 3, careNeeds: 4, disclosure: 5, lifestyle: 6, appendix: 7 }
    : { medication: 1, workplace: 2, careNeeds: 3, disclosure: 4, lifestyle: 5, appendix: 6 };

  const handleSectionCompletion = useCallback(() => {
    if (!canEdit) return Promise.resolve();

    markRegenHandled();

    const currentSectionData = patientDataModel?.dataModel?.['5_postAssessmentDiscussion']?.sections?.[sectionKey];
    const isAlreadyCompleted = currentSectionData?.status === 'completed';
    const sectionsAlreadyGenerated = patientDataModel?.dataModel?.['6_manageReports']?.sections?.['4']?.stageSectionsGenerated;
    const isCurrentSectionGenerated = sectionsAlreadyGenerated?.includes(currentSectionTitle) || false;

    const needsStatusWrite = !isAlreadyCompleted;
    const needsGeneration = hasWnaParagraphToGenerate
      && (hasChanges || !isCurrentSectionGenerated);

    if (!needsStatusWrite && !needsGeneration) return Promise.resolve();

    if (needsStatusWrite) {
      updatePatientFirestore(patientId, {
        '5_postAssessmentDiscussion': {
          sections: {
            [sectionKey]: {
              sectionTitle: currentSectionTitle,
              sectionType,
              status: 'completed',
              progress: 100,
            },
          },
        }
      });
    }

    const dataReady = flushPendingUpdates({ noTimeout: true });

    if (needsGeneration) {
      TriggerGenerateDocumentSection(patientId, currentStageTitle, currentSectionTitle, [], dataReady);
    }

    return dataReady;
  }, [canEdit, patientDataModel, hasChanges, hasWnaParagraphToGenerate, patientId,
      currentStageTitle, currentSectionTitle, updatePatientFirestore, flushPendingUpdates, markRegenHandled]);

  const handleFinish = useCallback(async () => {
    if (isNavigating) return;
    document.activeElement?.blur();
    setIsSaving(true);
    try {
      await handleSectionCompletion();
      setIsDialogOpen(true);
    } catch (error) {
      console.error('Error completing Next Steps section:', error);
    } finally {
      setIsSaving(false);
    }
  }, [isNavigating, handleSectionCompletion]);

  const handleGenerateReport = useCallback(() => {
    setIsDialogOpen(false);
    setIsGenerating(true);
  }, []);

  const handleCancelGeneration = useCallback(() => {
    setIsGenerating(false);
  }, []);

  // Marks the PAD module as complete and navigates to the CYP Documents Dashboard.
  const handleNextPageButtonClick = useCallback(async () => {
    const manageReports = patientDataModel?.dataModel?.['6_manageReports'];
    const firstCompletedFields = buildFirstCompletedFields(patientDataModel?.dataModel?.['5_postAssessmentDiscussion']);

    await updatePatientFirestore(patientId, {
      '5_postAssessmentDiscussion': buildPadCompletionFields(patientDataModel, firstCompletedFields),
      '6_manageReports': {
        padReportGenerated: true,
        padReportGeneratedAt: serverTimestamp(),
        padReportGeneratedBy: auth.currentUser.uid,
        padReportGeneratedByName: auth.currentUser.displayName,
        ...buildFirstGeneratedFields('pad', manageReports),
      }
    });

    appendReportAuditEntry(patientId, {
      module: 'pad',
      eventType: 'generated',
      isFirstOccurrence: !manageReports?.padReportFirstGeneratedAt,
    });

    recordInferredAppointmentDateOnCompletion({
      patientId,
      moduleKey: 'pad',
      didWriteFirstCompleted: Object.keys(firstCompletedFields).length > 0,
      enabled: isAppointmentDatesEnabled,
    });

    // CYP documents dashboard lives under the /cyp-adhd/ namespace.
    navigate(`/patient/${patientId}/cyp-adhd/documents`);
  }, [patientId, patientDataModel, updatePatientFirestore, navigate, isAppointmentDatesEnabled]);

  const handleBack = useCallback(() => {
    if (isNavigating) return;
    document.activeElement?.blur();
    setIsNavigating(true);
    handleSectionCompletion().then(() => {
      navigate(`/patient/${patientId}${prevSectionLink}`);
    });
  }, [isNavigating, patientId, navigate, prevSectionLink, handleSectionCompletion]);

  // Side navigation — simplified two-entry sidebar for the TOC flow.
  const SECTIONS_PAD = useMemo(() => {
    return [
      { sectionTitle: 'Assessment Outcome', route: '/proforma/cyp-adhd/post-assessment-discussion/review-assessment', status: 'completed', isUnlocked: true },
      { sectionTitle: 'Next Steps', route: '/proforma/cyp-adhd/post-assessment-discussion/next-steps', status: isFormComplete ? 'completed' : 'in-progress', isUnlocked: true },
    ];
  }, [isFormComplete]);

  const navigateToSection = useCallback((index) => {
    if (isNavigating) return;
    document.activeElement?.blur();
    setIsNavigating(true);
    handleSectionCompletion().then(() => {
      const section = SECTIONS_PAD[index];
      if (section.route) {
        navigate(`/patient/${patientId}${section.route}`);
      }
    });
  }, [isNavigating, patientId, navigate, SECTIONS_PAD, handleSectionCompletion]);

  const prevSectionTitle = 'Assessment Outcome';

  const breadcrumbTuples = [
    ["Patient Overview", `/patient/${patientId}/cyp-adhd`],
    ["Post-Assessment Discussion"],
    ["Next Steps"],
  ];

  // The TOC always has exactly 2 pages: Assessment Outcome (1 of 2) and
  // Next Steps (2 of 2).
  const counterText = '2 of 2';

  return (
    <Layout activePage="dashboard" scrollable={false}>
      <div className="flex flex-col h-full min-h-0">
        <div className="flex-shrink-0">
          <Breadcrumb tupleList={breadcrumbTuples} />
        </div>
        <div className="flex flex-1 min-h-0">
          <PADSideNavbar
            sections={SECTIONS_PAD}
            currentSectionIndex={1}
            navigateToSection={navigateToSection}
            isNavigating={isNavigating || isSaving}
          />
          <div className="flex-1 flex flex-col min-h-0">
            <div className="flex-1 overflow-y-auto p-8 text-left">
              {/* Section Title */}
              <div className="flex justify-between items-center gap-3 mb-6">
                <h2 className="text-2xl font-bold">{displayNumber}. {currentSectionTitle}</h2>
            <PatientContractTypeBadge
              label="Contract"
              organisation={claims?.organisation}
              assessmentType={patientDataModel?.customFields?.assessmentType}
              subContractType={patientDataModel?.customFields?.subContractType}
            />
          </div>

          {/* Medication Decision panel */}
          <div className="p-8 my-8 bg-gray-50 rounded-2xl border border-gray-200 text-left">
            <h3 className="text-left text-slate-700 text-xl font-semibold mb-6">
              {displayNumber}.{panelNumbers.medication} Medication Decision
            </h3>
            <div className="space-y-4">
              <DecisionCard
                question="Does patient currently want to consider ADHD medication?"
                value={decisions.wantsMedication}
                onChange={(val) => handleDecision('wantsMedication', val)}
                disabled={!canEdit}
              />
            </div>
          </div>

          {/* Workplace Needs Assessment panel */}
          <div className="p-8 my-8 bg-gray-50 rounded-2xl border border-gray-200 text-left">
            <h3 className="text-left text-slate-700 text-xl font-semibold mb-6">
              {displayNumber}.{panelNumbers.workplace} Workplace Needs Assessment
            </h3>
            <div className="space-y-4">
              <DecisionCard
                question="Would patient benefit from Workplace Needs Assessment?"
                value={isWorkplaceNeedsAssessmentContract ? decisions.benefitsFromWorkplaceAssessment : null}
                onChange={(val) => handleDecision('benefitsFromWorkplaceAssessment', val)}
                disabled={!canEdit || !isWorkplaceNeedsAssessmentContract}
                inactive={!isWorkplaceNeedsAssessmentContract}
                tooltip={!isWorkplaceNeedsAssessmentContract ? 'Only applicable for Lexxic contract patients.' : undefined}
              />

              <div
                className="overflow-hidden transition-all duration-500 ease-in-out"
                style={{
                  maxHeight: (isWorkplaceNeedsAssessmentContract && decisions.benefitsFromWorkplaceAssessment === false) ? '300px' : '0px',
                  opacity: (isWorkplaceNeedsAssessmentContract && decisions.benefitsFromWorkplaceAssessment === false) ? 1 : 0,
                }}
              >
                <div className="mt-4 text-left">
                  <h4 className="text-gray-700 text-sm font-semibold mb-2">
                    Please explain why a Workplace Needs Assessment is not recommended for this patient
                    <span className="text-red-600 ml-1">*</span>
                  </h4>
                  <DictationTextArea
                    value={wnaDeclinedRationale}
                    onChange={(text) => {
                      setWnaDeclinedRationale(text);
                      setHasChanges(true);
                    }}
                    onBlur={handleWnaRationaleBlur}
                    canEdit={canEdit}
                    placeholder="e.g. The patient is not currently employed or seeking employment, therefore a Workplace Needs Assessment would not be beneficial at this time..."
                    ariaLabel="Rationale for declining Workplace Needs Assessment"
                    rows={4}
                    minHeight="8rem"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Autism Follow-Up panel */}
          {showAutismFollowUpPanel && (
            <div className="p-8 my-8 bg-gray-50 rounded-2xl border border-gray-200 text-left">
              <h3 className="text-left text-slate-700 text-xl font-semibold mb-6">
                {displayNumber}.{panelNumbers.autismFollowUp} Autism Follow-Up
              </h3>
              <div className="space-y-4">
                <DecisionCard
                  question="Include autism follow-up instructions in the report?"
                  value={isAutismFollowUpActive ? decisions.includeAutismFollowUp : null}
                  onChange={(val) => handleDecision('includeAutismFollowUp', val)}
                  disabled={!canEdit || !isAutismFollowUpActive}
                  inactive={!isAutismFollowUpActive}
                  tooltip={!isAutismFollowUpActive
                    ? 'This only applies when autism is selected as an emerging differential.'
                    : 'When set to Yes, the report will include a boilerplate paragraph advising the patient to seek a follow-up autism assessment.'}
                />
              </div>
            </div>
          )}

          {/* Care Needs Assessment — always shown. This page is positive-ADHD
              only, and the care-needs paragraph lives only in that report
              variant. Yes inserts the static local-council paragraph; No
              omits it. There is no free-text rationale on No. */}
          <div className="p-8 my-8 bg-gray-50 rounded-2xl border border-gray-200 text-left">
            <h3 className="text-left text-slate-700 text-xl font-semibold mb-6">
              {displayNumber}.{panelNumbers.careNeeds} Care Needs Assessment
            </h3>
            <div className="space-y-4">
              <DecisionCard
                question="Would the patient benefit from a care needs assessment?"
                value={decisions.includeCareNeedsAssessment}
                onChange={(val) => handleDecision('includeCareNeedsAssessment', val)}
                disabled={!canEdit}
                tooltip="When set to Yes, the report includes a paragraph explaining how to apply for a care needs assessment from the local council."
              />
            </div>
          </div>

          {/* Disclosure panel */}
          <div className="p-8 my-8 bg-gray-50 rounded-2xl border border-gray-200 text-left">
            <h3 className="text-left text-slate-700 text-xl font-semibold mb-6">
              {displayNumber}.{panelNumbers.disclosure} Disclosure
            </h3>
            <div className="space-y-4">
              <DecisionCard
                question='Include "Sharing with Friends and Family" in report?'
                value={decisions.includeDisclosureFriendsFamily}
                onChange={(val) => handleDecision('includeDisclosureFriendsFamily', val)}
                disabled={!canEdit}
              />

              <DecisionCard
                question='Include "Sharing at Work" in report?'
                value={decisions.includeDisclosureWork}
                onChange={(val) => handleDecision('includeDisclosureWork', val)}
                disabled={!canEdit}
              />

              <DecisionCard
                question='Include "Sharing in Education" in report?'
                value={decisions.includeDisclosureEducation}
                onChange={(val) => handleDecision('includeDisclosureEducation', val)}
                disabled={!canEdit}
              />
            </div>
          </div>

          {/* Lifestyle Recommendations panel */}
          <div className="p-8 my-8 bg-gray-50 rounded-2xl border border-gray-200 text-left">
            <h3 className="text-left text-slate-700 text-xl font-semibold mb-6">
              {displayNumber}.{panelNumbers.lifestyle} Lifestyle Recommendations
            </h3>
            <div className="space-y-4">
              <DecisionCard
                question='Include "Exercise and Diet" in report?'
                value={decisions.includeExerciseDiet}
                onChange={(val) => handleDecision('includeExerciseDiet', val)}
                disabled={!canEdit}
              />

              <DecisionCard
                question='Include "Relationships and Partners" in report?'
                value={decisions.includeRelationships}
                onChange={(val) => handleDecision('includeRelationships', val)}
                disabled={!canEdit}
              />

              <DecisionCard
                question='Include "Driving" in report?'
                value={decisions.includeDriving}
                onChange={(val) => handleDecision('includeDriving', val)}
                disabled={!canEdit}
              />
            </div>
          </div>

          {/* Appendix Inclusions panel */}
          <div className="p-8 my-8 bg-gray-50 rounded-2xl border border-gray-200 text-left">
            <h3 className="text-left text-slate-700 text-xl font-semibold mb-6">
              {displayNumber}.{panelNumbers.appendix} "Further Information and Resources" Inclusions
            </h3>
            <div className="space-y-4">
              <DecisionCard
                question='Include "Females with ADHD" in report?'
                value={decisions.includeFemalesWithADHD}
                onChange={(val) => handleDecision('includeFemalesWithADHD', val)}
                disabled={!canEdit}
                subtitleHint={patientSexAtBirth ? `Patient's sex assigned at birth: ${patientSexAtBirth}` : null}
                onSourceClick={sexAtBirthRawPrefillData.length > 0 ? () => setIsSexDialogOpen(true) : undefined}
              />

              <DecisionCard
                question='Include "Educational Advice and Accommodations" in report?'
                value={decisions.includeEducationalAdvice}
                onChange={(val) => handleDecision('includeEducationalAdvice', val)}
                disabled={!canEdit}
              />

              <DecisionCard
                question='Include "Employment Advice" in report?'
                value={decisions.includeEmploymentAdvice}
                onChange={(val) => handleDecision('includeEmploymentAdvice', val)}
                disabled={!canEdit}
              />
            </div>
          </div>

            </div>

            {/* Pinned footer navigation -- matches the CYP CI / Adult Autism
                modular proforma pattern. Next Steps is always the last page
                in the TOC flow, so there is no "Go to next" button; only
                Back and Finish. */}
            <ModularStepFooter
              showBack
              backLabel={`Back to ${prevSectionTitle}`}
              onBack={handleBack}
              backDisabled={isNavigating}
              counterText={counterText}
              rightActions={
                canEdit ? (
                  <button
                    onClick={handleFinish}
                    disabled={!isFormComplete || isSaving || isNavigating}
                    title={!isFormComplete
                      ? 'Please answer all questions before finishing'
                      : undefined}
                    className={`flex items-center py-2 px-4 rounded-md shadow-sm transition-all duration-200
                      ${(!isFormComplete || isSaving || isNavigating)
                        ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        : 'bg-primary text-white hover:bg-primary/90'}`}
                  >
                    <span className="font-semibold text-sm">
                      {isSaving ? 'Saving Progress...' : 'Finish Post-Assessment Discussion'}
                    </span>
                    {isSaving
                      ? <ButtonSpinner className="w-5 h-5 ml-2" />
                      : <CheckCircleIcon className="w-5 h-5 ml-2" aria-hidden="true" />}
                  </button>
                ) : undefined
              }
            />
          </div>
        </div>
      </div>

      {/* Source-data dialog for "Sex assigned at birth" */}
      <Dialog.Root open={isSexDialogOpen} onOpenChange={setIsSexDialogOpen}>
        <Dialog.Portal>
          <Dialog.Overlay className="radix-dialog-overlay-motion fixed inset-0 bg-black/50" />
          <Dialog.Content className="radix-dialog-content-centered-motion fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-white p-6 rounded-lg shadow-lg w-[700px] max-h-[65vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <Dialog.Title className="text-base font-semibold text-gray-900">
                Patient Form Responses
              </Dialog.Title>
              <Dialog.Close asChild>
                <button className="p-1 rounded-full hover:bg-gray-100">
                  <XIcon className="h-5 w-5 text-gray-500" />
                </button>
              </Dialog.Close>
            </div>

            <div className="mb-4">
              <h3 className="font-semibold text-sm mb-2">Sex Assigned at Birth</h3>

              <div className="space-y-2 mt-2">
                {sexAtBirthRawPrefillData.length > 0 ? (
                  sexAtBirthRawPrefillData.map((item, i) => (
                    <div key={i} className={`border rounded-md p-3 ${
                      item.hasAnswer
                        ? 'bg-violet-50 shadow-sm border-violet-200'
                        : 'bg-gray-50 border-dashed border-gray-200 opacity-75'
                    }`}>
                      <div className="text-xs text-gray-500 mb-0.5">From: {item.formName}</div>

                      {item.questionTitle && (
                        <div className="text-xs font-medium text-gray-700 mb-1">{item.questionTitle}</div>
                      )}

                      <div className="text-xs font-medium mb-1.5">{item.question}</div>

                      <div className={`p-2 rounded border whitespace-pre-wrap text-xs ${
                        item.hasAnswer
                          ? 'bg-white border-gray-200'
                          : 'bg-gray-100 border-gray-200 border-dashed text-gray-500 italic'
                      }`}>
                        <FormattedAnswer
                          rawAnswer={item.rawAnswer}
                          displayText={item.answer}
                        />
                      </div>

                      {item.options && item.options.length > 0 && (
                        <div className="mt-2">
                          <div className="text-xs text-gray-500 mb-1">Available options:</div>
                          <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5">
                            {item.options.map((option, optIndex) => {
                              const selected = item.hasAnswer && isOptionSelected(option, item.rawAnswer);
                              return (
                                <div
                                  key={optIndex}
                                  className={`text-xs p-1 rounded ${
                                    selected
                                      ? 'bg-violet-100 border border-violet-300 text-violet-700 font-medium'
                                      : 'bg-gray-50 border border-gray-200 text-gray-500'
                                  }`}
                                >
                                  {option}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  ))
                ) : (
                  <p className="text-gray-500 italic">No source data available</p>
                )}
              </div>
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>

      <ModuleCompletionDialog
        open={isDialogOpen && !isGenerating}
        onOpenChange={setIsDialogOpen}
        title="Post-Assessment Discussion Complete"
        width={600}
        actions={
          <>
            <CancelButton onClick={() => setIsDialogOpen(false)} />
            <GenerateReportButton onClick={handleGenerateReport} />
          </>
        }
      >
        <SummarySection title="Summary:">
          The Post-Assessment Discussion has been completed. Would you like to generate the report?
        </SummarySection>
      </ModuleCompletionDialog>

      <GenerateReportProgressDialog
        reportStage="CYP ADHD Post-Assessment Discussion"
        patientId={patientId}
        clinicianId={auth.currentUser?.uid || ''}
        handleNextPageButtonClick={handleNextPageButtonClick}
        isGenerating={isGenerating}
        onCancel={handleCancelGeneration}
      />
    </Layout>
  );
}


function isOptionSelected(option, rawAnswer) {
  if (!rawAnswer) return false;
  if (typeof rawAnswer === 'string') return rawAnswer === option;
  if (Array.isArray(rawAnswer) && typeof rawAnswer[0] === 'string') return rawAnswer.includes(option);
  if (Array.isArray(rawAnswer) && typeof rawAnswer[0] === 'object') return rawAnswer.some(a => a.value === option);
  if (rawAnswer && typeof rawAnswer === 'object' && 'value' in rawAnswer) return rawAnswer.value === option;
  return false;
}


/**
 * DecisionCard -- single Yes/No toggle row for a report-inclusion decision.
 * Identical to the adult 6_PAD_TOC.js DecisionCard so the UX is consistent.
 */
function DecisionCard({ question, value, onChange, disabled, tooltip, inactive, hint, subtitleHint, onSourceClick }) {
  const isDisabled = disabled || inactive;

  const options = [
    { label: 'Yes', matches: true },
    { label: 'No', matches: false },
  ];

  return (
    <div className="mb-4 flex items-start">
      <div className="w-5 shrink-0 mt-0.5">
        {tooltip && (
          <InfoTooltip
            content={tooltip}
            label={`Information about ${question}`}
            maxWidth="300px"
          />
        )}
      </div>

      <div className={`flex items-start transition-opacity duration-200 ${inactive ? 'opacity-50' : ''}`}>
        <div className="w-[22rem] shrink-0 ml-2">
          <span className="text-left font-semibold text-gray-700">{question}</span>

          {subtitleHint && (
            <div className="flex items-center gap-1 mt-0.5">
              <p className="text-xs text-gray-400 italic">{subtitleHint}</p>
              {onSourceClick && (
                <div
                  className="cursor-pointer"
                  onClick={onSourceClick}
                  role="button"
                  tabIndex={0}
                  aria-label="View source form answer"
                  onKeyDown={(e) => e.key === 'Enter' && onSourceClick()}
                >
                  <div className="flex items-center gap-1 text-xs text-violet-800 font-medium rounded-full px-1 py-1 transition-all duration-200 hover:bg-violet-100">
                    <SearchIcon className="h-3 w-3" />
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="ml-5 shrink-0 flex flex-col gap-1.5">
          <ul className="items-center flex flex-row gap-4">
            {options.map((option) => {
              const isSelected = value === option.matches;

              return (
                <li
                  key={option.label}
                  onClick={() => !isDisabled && onChange(isSelected ? null : option.matches)}
                  className={`
                    p-2 w-64 shrink-0
                    rounded-lg transition-all duration-300 ease-in-out
                    text-center
                    flex flex-row items-center justify-center gap-2
                    ${isDisabled
                      ? 'cursor-not-allowed opacity-75'
                      : 'cursor-pointer group'
                    }
                    ${isSelected
                      ? 'bg-violet-600 border-2 border-violet-800 font-semibold shadow-lg transform scale-105'
                      : !isDisabled
                        ? 'bg-white hover:bg-gray-50 border-2 border-dashed border-gray-300 hover:border-gray-400 shadow-sm hover:shadow-md'
                        : 'bg-white border-2 border-dashed border-gray-300 shadow-sm'
                    }
                  `}
                >
                  <div className={`transition-all duration-300 ease-in-out ${isSelected ? 'text-white' : 'text-gray-400 group-hover:text-gray-600'}`}>
                    {isSelected ? (
                      <CheckIcon className="w-4 h-4" />
                    ) : (
                      <PlusIcon className="w-4 h-4" />
                    )}
                  </div>
                  <span className={`font-semibold transition-all duration-300 ease-in-out ${isSelected ? 'text-white' : 'text-gray-700 group-hover:text-gray-900'}`}>
                    {option.label}
                  </span>
                </li>
              );
            })}
          </ul>
          {hint && (
            <p className="text-xs text-gray-400 italic ml-1">{hint}</p>
          )}
        </div>
      </div>
    </div>
  );
}
